/**
 * progress.ts — Which levels the player has finished.
 *
 * Kept in `localStorage`, which is the right size of tool for the job: a set of
 * small integers that should survive a refresh and matter to nobody but the
 * person at the keyboard. No account, no server, no expiry.
 *
 * **Every path here tolerates storage being unavailable.** Safari's private
 * mode, a browser with cookies disabled, and a Capacitor webview with a
 * cleared profile all throw from `localStorage` rather than returning null, and
 * a game that refuses to start because it can't remember your progress would be
 * a poor trade. When storage fails the campaign simply forgets — everything is
 * still playable, and the menu shows no ticks.
 */

const KEY = 'dogwalker.completed';

/** The levels finished so far. Empty when nothing is stored or storage is off. */
export function completedLevels(): Set<number> {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    // Anything unexpected in there — a hand-edited value, a format from a
    // future version — is discarded rather than trusted.
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((n): n is number => Number.isInteger(n) && n > 0));
  } catch {
    return new Set();
  }
}

/** Record a level as finished. Silently does nothing if storage is unavailable. */
export function markCompleted(level: number): void {
  const done = completedLevels();
  done.add(level);
  setCompleted(done);
}

/**
 * Replace the whole set at once.
 *
 * What cloud save writes back after merging a device's levels with the
 * account's. Kept here rather than in `cloud/` so this module stays the only
 * thing that knows the key or the on-disk format.
 */
export function setCompleted(levels: Iterable<number>): void {
  const clean = [...new Set(levels)].filter((n) => Number.isInteger(n) && n > 0).sort((a, b) => a - b);
  try {
    window.localStorage.setItem(KEY, JSON.stringify(clean));
  } catch {
    /* nothing worth doing: the run still counted, it just won't be remembered */
  }
}

/** Forget everything. Used by the menu's reset and by tests. */
export function clearProgress(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* see above */
  }
}
